import { useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { BusinessConnectionMstSetting } from '../features/business-connection-mst/models';
import { fetchBusinessConnectionMstSetting } from '../features/business-connection-mst/repositories';
import type { LeaseCompanyMstSetting } from '../features/lease-company-mst/models';
import { fetchLeaseCompanyMstSetting } from '../features/lease-company-mst/repositories';
import type { LocationMstSetting } from '../features/location-mst/models';
import { fetchLocationMstSetting } from '../features/location-mst/repositories';
import type { SegmentMstSetting } from '../features/segment-mst/models';
import { fetchSegmentMstSettings } from '../features/segment-mst/repositories';

interface MstSettings {
  location: LocationMstSetting;
  businessConnection: BusinessConnectionMstSetting;
  leaseCompany: LeaseCompanyMstSetting;
  segmentMsts: SegmentMstSetting[];
}

interface MstSettingsContextProps extends MstSettings {
  refetch: () => Promise<void>;
}

const MstSettingsContext = createContext<MstSettingsContextProps | null>(null);

function MstSettingsProvider({ children }: { children: ReactNode }) {
  const [mstSettings, setMstSettings] = useState<MstSettings | null>(null);
  const { tryRequest } = useTry();

  const fetchData = useCallback(
    async () =>
      await tryRequest(async () => {
        const [location, businessConnection, leaseCompany, segmentMsts] = await Promise.all([
          fetchLocationMstSetting(),
          fetchBusinessConnectionMstSetting(),
          fetchLeaseCompanyMstSetting(),
          fetchSegmentMstSettings(),
        ]);
        setMstSettings({
          location,
          businessConnection,
          leaseCompany,
          segmentMsts: segmentMsts.toSorted((a, b) => a.segmentMstKey - b.segmentMstKey),
        });
      }),
    [],
  );

  useEffect(() => void fetchData(), [fetchData]);

  return mstSettings ? (
    <MstSettingsContext.Provider value={{ ...mstSettings, refetch: fetchData }}>
      {children}
    </MstSettingsContext.Provider>
  ) : null;
}

function useMstSettings() {
  const ctx = useContext(MstSettingsContext);
  if (!ctx) {
    throw new Error('Context does not exist. Error occurred when fetching mst settings.');
  }

  return ctx;
}

export type { MstSettingsContextProps };
export { MstSettingsProvider, useMstSettings };
